import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";

const STEP_CONFIG = [
  {
    icon: "solar:eye-bold-duotone",
    color: "bg-indigo-600",
    glow: "shadow-indigo-500/30",
    defaultTitle: "Comprehensive Eye Assessment",
    defaultDesc: "Book an initial consultation with our vision specialists. We perform a detailed diagnostic evaluation of your binocular vision, eye tracking, focus, and depth perception to pinpoint the exact nature of your condition.",
  },
  {
    icon: "solar:clipboard-heart-bold-duotone",
    color: "bg-sky-500",
    glow: "shadow-sky-500/30",
    defaultTitle: "Your Personalised Therapy Plan",
    defaultDesc: "Based on your diagnosis, our certified optometrists design a structured vision therapy programme. Sessions are tailored to your age, condition, and lifestyle — combining in-clinic exercises with guided home activities.",
  },
  {
    icon: "solar:chart-bold-duotone",
    color: "bg-emerald-500",
    glow: "shadow-emerald-500/30",
    defaultTitle: "Progress Monitoring & Recovery",
    defaultDesc: "Your therapist tracks your improvements at every session using measurable benchmarks. We adjust your programme in real time to maximise results and ensure you achieve long-lasting, sustainable vision health.",
  },
];

const HowDoesItWork = () => {
  const [data, setData] = useState({});

  useEffect(() => {
    fetch(`https://servercareseebd.careseebd.com/api/v1/steps/getSteps`)
      .then((r) => r.json())
      .then((d) => { if (d?.data?.length) setData(d.data[0]); });
  }, []);

  const steps = [
    {
      title: data?.firstStepsTitle || STEP_CONFIG[0].defaultTitle,
      desc: data?.firstStepsDes || STEP_CONFIG[0].defaultDesc,
      ...STEP_CONFIG[0],
    },
    {
      title: data?.secondStepsTitle || STEP_CONFIG[1].defaultTitle,
      desc: data?.secondStepsDes || STEP_CONFIG[1].defaultDesc,
      ...STEP_CONFIG[1],
    },
    {
      title: data?.thirdStepsTitle || STEP_CONFIG[2].defaultTitle,
      desc: data?.thirdStepsDes || STEP_CONFIG[2].defaultDesc,
      ...STEP_CONFIG[2],
    },
  ];

  return (
    <section className="section-py bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-widest mb-5">
            <Icon icon="solar:routing-bold" className="w-3.5 h-3.5" />
            How It Works
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
            {data?.title || "Your Vision Therapy Journey"}
          </h2>
          <p className="mt-5 text-base text-gray-500 leading-relaxed">
            {data?.description || "Getting started with vision therapy is simple. Our structured, evidence-based process ensures you receive the right care at every stage — from your first assessment to full recovery."}
          </p>
        </div>

        {/* Steps row */}
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8">

          {/* Dashed connector (desktop only) */}
          <div
            className="hidden md:block absolute top-10 left-[calc(33.33%+0px)] right-[calc(33.33%+0px)] h-0 border-t-2 border-dashed border-gray-200 pointer-events-none"
            style={{ top: "2.5rem" }}
          />

          {steps.map((step, i) => (
            <div key={i} className="group flex flex-col items-center text-center">
              {/* Icon tile */}
              <div className={`relative z-10 w-20 h-20 rounded-2xl ${step.color} flex flex-col items-center justify-center gap-0.5 shadow-xl ${step.glow} group-hover:-translate-y-1.5 transition-transform duration-300 mb-7`}>
                <Icon icon={step.icon} className="w-8 h-8 text-white" />
                <span className="text-[9px] font-bold text-white/60 uppercase tracking-widest">
                  Step {i + 1}
                </span>
              </div>

              <h3 className="font-heading text-lg font-bold text-gray-900 mb-3 tracking-tight">
                {step.title}
              </h3>
              <p className="text-sm text-gray-500 leading-relaxed max-w-[260px]">
                {step.desc}
              </p>
            </div>
          ))}
        </div>


      </div>
    </section>
  );
};

export default HowDoesItWork;
